import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { useMutation } from "@tanstack/react-query";
import { reorderWorkoutItems } from "api-client";
import { Icon } from "icons";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "ui";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import { MinimapWorkoutItemsList } from "src/workouts/MinimapWorkoutItemsList";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import { useWorkoutPermissions } from "src/workouts/WorkoutItemsPermission";

import { useMinimapDragAndDropManager } from "./MinimapDragAndDropManager";
import { MinimapExerciseItem } from "./MinimapExerciseItem";
import { MinimapSectionItem } from "./MinimapSectionItem";
import { type MinimapInstruction } from "./minimapUtils";

export const WorkoutMinimapContent = observer(
  function WorkoutMinimapContent(props: { className?: string }) {
    const { t } = useTranslation();
    const workoutEditStore = useWorkoutEditStore();
    const manager = useMinimapDragAndDropManager();
    const permissions = useWorkoutPermissions();
    const { startSaving } = useAuthenticatedNavigationContext();

    const reorderMutation = useMutation({
      mutationFn: (arg: {
        type: MinimapInstruction;
        itemId: string;
        targetId: string;
        workoutId: string;
      }) => {
        return reorderWorkoutItems({
          path: { workoutId: arg.workoutId },
          body: {
            type: arg.type,
            itemId: arg.itemId,
            targetId: arg.targetId,
          },
        });
      },
    });

    useEffect(() => {
      return monitorForElements({
        canMonitor: ({ source }) => {
          if (!permissions.canEditItems) {
            return false;
          }

          return source.data.contextId === manager.id;
        },
        async onDrop(args) {
          const { location, source } = args;

          if (!location.current.dropTargets.length) {
            return;
          }

          const itemId = source.data.id as string;
          const target = location.current.dropTargets[0];
          const targetId = target.data.id as string;
          const instruction = manager.getInstructionForTarget(targetId);

          if (instruction !== null) {
            manager.applyDrop(itemId, targetId, instruction);

            const stop = startSaving();
            reorderMutation.mutate(
              {
                type: instruction,
                itemId,
                targetId,
                workoutId: workoutEditStore.workout.id,
              },
              {
                onSuccess: () => {
                  stop();
                },
                onError: () => {
                  stop("error");
                },
              },
            );
          }

          manager.endDrag();
        },
      });
    }, [permissions.canEditItems, reorderMutation, startSaving]);

    const layoutItems = workoutEditStore.layout.items;

    return (
      <div className={cn("flex flex-col h-full bg-white", props.className)}>
        <div className="flex items-center justify-between px-2 py-2 border-b bg-muted">
          <div className="flex items-center gap-1">
            <button
              className="text-slate-400 hover:text-slate-600 transition-colors"
              onClick={() => {
                if (manager.allExpanded) {
                  manager.collapseAll();
                } else {
                  manager.expandAll();
                }
              }}
            >
              {manager.allExpanded ? (
                <Icon name="tabler-chevron-down" size={14} />
              ) : (
                <Icon name="tabler-chevron-right" size={14} />
              )}
            </button>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t("editWorkout.minimap.title")}
            </h3>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-slate-500">
              {layoutItems.length}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-2 px-1">
          {layoutItems.length > 0 ? (
            <div className="flex flex-col gap-2">
              <MinimapWorkoutItemsList
                renderGroup={(args) => {
                  return (
                    <MinimapSectionItem
                      id={args.id}
                      level={args.level}
                      index={args.index}
                      isLast={args.isLast}
                      childCount={args.childCount}
                      previousItemId={args.previousItemId}
                    >
                      {args.children}
                    </MinimapSectionItem>
                  );
                }}
                renderNormal={(args) => {
                  return (
                    <MinimapExerciseItem
                      id={args.id}
                      level={args.level}
                      index={args.index}
                      isLast={args.isLast}
                      previousItemId={args.previousItemId}
                    />
                  );
                }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-xs text-slate-400">
              {t("editWorkout.noExercises")}
            </div>
          )}
        </div>
      </div>
    );
  },
);
